import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { User, Lock, Save, Camera } from "lucide-react";
import api, { formatError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput, scorePassword } from "@/components/PasswordInput";

export default function Profile() {
  const { t } = useTranslation();
  const { user, setUser, loading } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState({ name: "", email: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingP, setSavingP] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    setProfile({ name: user.name || "", email: user.email || "" });
  }, [user, loading, nav]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingP(true);
    try {
      const { data } = await api.put("/auth/profile", profile);
      setUser(data);
      toast.success(t("profile.updated"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setSavingP(false);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error(t("profile.mismatch")); return; }
    if (scorePassword(pwd.new_password) < 3) { toast.error(t("pwdWeak")); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(t("profile.pwdChanged"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setSavingPwd(false);
  };

  if (loading || !user) return <div className="min-h-[60vh] flex items-center justify-center text-canard">Chargement...</div>;

  const uploadAvatar = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.put("/auth/profile", { avatar_url: up.data.url });
      setUser(data);
      toast.success(t("profile.photoUpdated"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="section-sand min-h-screen py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 space-y-6">
        <h1 className="font-heading font-extrabold text-3xl text-ink">{t("profile.title")}</h1>

        <div className="bg-white rounded-2xl shadow-card border border-border p-6 flex items-center gap-5">
          <div className="relative">
            {user.avatar_url ? (
              <img src={fileUrl(user.avatar_url)} alt="avatar" className="w-20 h-20 rounded-full object-cover border-2 border-pastel" data-testid="profile-avatar" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-canard/10 flex items-center justify-center text-canard font-heading font-extrabold text-2xl" data-testid="profile-avatar-placeholder">{(user.name || "?").charAt(0).toUpperCase()}</div>
            )}
            <label data-testid="avatar-upload" className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-emeraude text-white flex items-center justify-center cursor-pointer shadow-cta hover:bg-emeraude-dark">
              <Camera className="w-4 h-4" /><input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
            </label>
          </div>
          <div>
            <p className="font-heading font-bold text-ink text-lg">{user.name}</p>
            <p className="text-sm text-muted2 font-body">{user.email}</p>
          </div>
        </div>

        <form onSubmit={saveProfile} className="bg-white rounded-2xl shadow-card border border-border p-6 space-y-4">
          <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><User className="w-5 h-5 text-canard" /> {t("profile.info")}</h2>
          <div>
            <label className="text-sm font-bold text-ink">{t("profile.name")}</label>
            <Input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1 h-11 rounded-xl bg-sand" />
          </div>
          <div>
            <label className="text-sm font-bold text-ink">{t("profile.email")}</label>
            <Input data-testid="profile-email" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-1 h-11 rounded-xl bg-sand" />
          </div>
          <Button type="submit" data-testid="profile-save" disabled={savingP} className="rounded-full bg-canard hover:bg-canard-dark font-bold"><Save className="w-4 h-4 mr-1" /> {savingP ? "..." : t("profile.save")}</Button>
        </form>

        <form onSubmit={savePassword} className="bg-white rounded-2xl shadow-card border border-border p-6 space-y-4">
          <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Lock className="w-5 h-5 text-canard" /> {t("profile.pwdSection")}</h2>
          <div>
            <label className="text-sm font-bold text-ink">{t("profile.current")}</label>
            <PasswordInput data-testid="pwd-current" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="mt-1 h-11 rounded-xl bg-sand" required />
          </div>
          <div>
            <label className="text-sm font-bold text-ink">{t("profile.newp")}</label>
            <PasswordInput data-testid="pwd-new" showStrength value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="mt-1 h-11 rounded-xl bg-sand" required />
          </div>
          <div>
            <label className="text-sm font-bold text-ink">{t("profile.confirm")}</label>
            <PasswordInput data-testid="pwd-confirm" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="mt-1 h-11 rounded-xl bg-sand" required />
          </div>
          <Button type="submit" data-testid="pwd-save" disabled={savingPwd} className="rounded-full bg-emeraude hover:bg-emeraude-dark font-bold">{savingPwd ? "..." : t("profile.savePwd")}</Button>
        </form>
      </div>
    </div>
  );
}
